import {ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {concatMap, interval, mergeMap, Subscription} from 'rxjs';
import {ToolsService} from '../../../../services/tools.service';
import {CssPositionEnum} from '../../../../enums/CssPositionEnum';
import {AppConfigService} from '../../../../services/app-config.service';
import {BankidService} from '../../../../services/bankid.service';
import {DeviceTypeEnum} from '../../../../enums/DeviceTypeEnum';
import {BankIdCollectHintCode, BankIdCollectStatus} from '../../../../enums/BankIdEnums';
import {MockBankidService} from '../../../../services/mocks/mock-bankid.service';
import {StorageService} from '../../../../services/storage.service';
import {SessionService} from '../../../../services/session.service';
import {MyPagesService} from '../../../../services/my-pages.service';

@Component({
  selector: 'app-bankid-section',
  templateUrl: './bankid-section.component.html',
  styleUrls: ['./bankid-section.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BankidSectionComponent implements OnInit, OnDestroy {
  public s3AssetUrl: string;
  public bankIdLogo: string;
  public qrIcon: string;
  public mockBankId = false;
  public startedLogin = false;
  public debugPno: string = null;
  public isMobileDevice = true;
  public isLoading = false;
  public waitingForQr = true;
  public qr: string | null;
  public loginCancelled = false;
  public error: string;
  @ViewChild('debugPnoInput') debugPnoInput: ElementRef;
  protected readonly CssPositionEnum = CssPositionEnum;
  private subscriptions: Subscription[] = [];
  private collectSubscription: Subscription;
  private getQrSubscription: Subscription;

  constructor(
    private tools: ToolsService,
    private appConfigService: AppConfigService,
    private bankIdService: BankidService,
    private changeDetectorRef: ChangeDetectorRef,
    private storageService: StorageService,
    private session: SessionService,
    private myPagesService: MyPagesService,
  ) {
    this.s3AssetUrl = `${appConfigService.appConfig.amazonS3Url}assets/`;
    this.bankIdLogo = `${this.s3AssetUrl}BankID_logo.svg`;
    this.qrIcon = `${this.s3AssetUrl}qr.svg`;
    if (typeof this.bankIdService['pno'] !== 'undefined') {
      this.mockBankId = true;
    }
    this.isMobileDevice = this.tools.getDeviceType() === DeviceTypeEnum.MOBILE;
  }

  public ngOnInit(): void {
    this.setListeners();
    this.debugPno = this.storageService.read('debugPno');
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  public async startBankIdLogin(sameDevice?: boolean): Promise<void> {
    const observer = {
      next: (res) => {
        if (sameDevice) {
          this.isLoading = true;
          window.location.href = `https://app.bankid.com/?autostarttoken=${res.autoStartToken}&redirect=null`;
        } else {
          this.qr = res.qr;
          this.waitingForQr = false;
          this.startGeneratingQrCodes();
        }

        this.startCollecting();
      },
      error: this.handleError,
    };

    const s = this.bankIdService.authenticate().subscribe(observer);
    this.subscriptions.push(s);
    this.startedLogin = true;
    this.saveDebugPno();
  }

  public saveDebugPno() {
    if (this.mockBankId) {
      const pno = this.debugPnoInput.nativeElement.value;
      (<MockBankidService>this.bankIdService).pno = pno;
      this.debugPno = pno;
      this.storageService.save('debugPno', pno);
    }
  }

  public cancelBankIdLogin(): void {
    this.loginCancelled = true;
    this.subscriptions.push(
      this.bankIdService.cancel().subscribe(() => {
        this.unsubscribeAll();
        this.qr = null;
        this.isLoading = false;
        this.startedLogin = false;
      }),
    );
  }

  private startGeneratingQrCodes() {
    const observer = {
      next: (res) => {
        this.qr = res.qr;
        this.changeDetectorRef.markForCheck();
      },
      error: this.handleError,
    };
    this.getQrSubscription = interval(1000)
      .pipe(mergeMap(() => this.bankIdService.getQr()))
      .subscribe(observer);
  }

  private startCollecting() {
    const observer = {
      next: (res) => {
        if (res.hintCode === BankIdCollectHintCode.userSign) {
          this.isLoading = true;
          this.qr = null;
          this.getQrSubscription?.unsubscribe();
        }
        if (res.status === BankIdCollectStatus.complete) {
          this.unsubscribeAll();
          return this.myPagesService
            .login(this.session.getSessionID())
            .pipe(concatMap(() => this.myPagesService.getProfile()))
            .subscribe();
        }
      },
      error: this.handleError,
    };
    this.collectSubscription = interval(2000)
      .pipe(mergeMap(() => this.bankIdService.collect()))
      .subscribe(observer);
  }

  private setListeners(): void {
    this.subscriptions.push();
  }

  private unsubscribeAll() {
    this.collectSubscription?.unsubscribe();
    this.getQrSubscription?.unsubscribe();
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private handleError = (err: any) => {
    console.error(err);
    this.error = err.error;
    this.qr = null;
    this.isLoading = false;
    this.startedLogin = false;
    this.unsubscribeAll();
  };
}
